<template>
	<div :id="uid" :class="['customer-forms-card', 'is-' + formState.class]">
		<div :class="['customer-forms-card__state', loading && 'is-disabled']">
			<i
				:class="['icon', 'icon-' + formState.icon]"
				ref="tooltipStatusRef"
				@mouseenter="tooltipStatusVisible = true"
				@focus="tooltipStatusVisible = true"
				@mouseleave="tooltipStatusVisible = false"
				@blur="tooltipStatusVisible = false"
			></i>
			<Tooltip
				:text="formState.title"
				v-if="formState.title"
				:trackedRefName="'tooltipStatusRef'"
				:display="tooltipStatusVisible"
				:offset="[4, 4]"
				position="top-start"
			/>
		</div>
		<div :class="['customer-forms-card__body', loading && 'is-disabled']">
			<div class="customer-forms-card__main">
				<p class="customer-forms-card__title">
					{{ form.id }}
					<span class="customer-forms-card__divider">|</span>
					<Ellipsis :data="form.name"></Ellipsis>
				</p>

				<!-- Per Product/Forms Team - DO NOT truncate Revision; need to see end -->
				<p class="customer-forms-card__revision">
					{{ form.formRevision }}
				</p>
			</div>
			<div class="customer-forms-card__type">
				{{ customerFormPrintType }}
			</div>
			<div class="customer-forms-card__full-container">
				<Ellipsis
					:data="form.description"
					class="customer-forms-card__description"
				></Ellipsis>
			</div>
			<span class="customer-forms-card__sync">
				{{ formStatus }}
			</span>
		</div>
		<div :class="['customer-forms-card__footer', loading && 'is-disabled']">
			<Toggle
				:disabled="!hasUpdatePermission"
				:label="form.enabled ? 'Enabled' : 'Disabled'"
				v-model="formLocal.enabled"
				@change="enableForm"
			/>
			<ActionsSet
				class="customer-forms-card__actions"
				:id="`${uid}_action`"
				:variant="variant"
				:options="actions"
			/>
		</div>
		<Spinner
			class="btn-spinner-overlay"
			v-if="loading"
			:processor="{ loading: true }"
			:variant="'infinity-scroll'"
			data-test="spinner"
		/>
	</div>
</template>

<script>
import Toggle from '@/components/forms/selection-control/toggle/toggle';
import Spinner from '@/components/visual/spinner/spinner';
import ActionsSet from '@/site-components/actions-set/actions-set';
import Tooltip from '@/components/shells/tooltip/tooltip';
import PdfFnForm from '@/mixins/forms/pdf-fn-form';
import Ellipsis from '@/components/visual/ellipsis.vue';

// vuex
import { mapActions } from 'vuex';

// libraries
import _ from 'lodash';

export default {
	name: 'CustomerFormsCard',
	mixins: [PdfFnForm],
	components: {
		ActionsSet,
		Tooltip,
		Toggle,
		Spinner,
		Ellipsis
	},
	data() {
		return {
			formLocal: _.cloneDeep(this.form),
			tooltipStatusVisible: false,
			tooltipTitleVisible: false,
			tooltipDescriptionVisible: false,
			loading: false,
			formId: undefined
		};
	},
	props: {
		form: {
			type: Object,
			default: () => {}
		}
	},
	created() {
		this.formId = this.form.id;
	},
	computed: {
		actions() {
			const operacionMode = this.hasUpdatePermission ? 'edit' : 'preview';
			return [
				{
					id: 1,
					action: {
						path: `/clients/${
							this.$route.params.customerId
						}/forms/${operacionMode}/${
							this.form.id
						}/${this.customerFormPrintType.toLowerCase()}`
					},
					title: 'Select to view details',
					iconClass: this.hasUpdatePermission
						? 'icon icon-edit'
						: 'icon icon-show',
					label: this.hasUpdatePermission ? 'Edit' : 'View Details',
					type: 'route-link',
					visible: true
				},
				{
					id: 3,
					action: {
						name: 'customer-form-history',
						path: `/clients/${this.$route.params.customerId}/forms/${this.formId}/history`,
						params: {
							changedEntityId: this.formId
						}
					},
					iconClass: 'icon icon-history',
					label: 'History',
					title: 'Select to go to form history',
					type: 'route-link',
					visible: true
				}
			];
		},
		customerFormPrintType() {
			return this.isLaserForm ? 'Laser' : 'Impact';
		},
		disabledPdfIcon() {
			return this.isLaserForm && !this.form.pdfData;
		},
		formState() {
			if (this.form.active) {
				return {
					class: 'active',
					icon: 'checkbox',
					title: 'Active'
				};
			}
			return {
				icon: 'check-indeterminate',
				title: 'Inactive'
			};
		},
		formStatus() {
			return this.form.status === 'U'
				? 'Unpublished'
				: this.form.status === 'P'
				? 'Pending Changes'
				: '';
		},
		isActive() {
			return this.form.enabled;
		},
		isLaserForm() {
			return this.form.formPrintType === 'L';
		},
		hasUpdatePermission() {
			return this.validatePermission('forms.update');
		},
		cardTitleTooltipText() {
			return `${this.form.id} | ${this.form.name}`;
		}
	},
	methods: {
		...mapActions('customerForms', [
			'updateFormStatus',
			'getFormsByCustomer'
		]),
		async enableForm(val) {
			let action = val ? 'enable' : 'disable';

			try {
				this.loading = true;
				this.$store.commit('customerForms/setUpdatingStatus', true);

				await this.updateFormStatus({
					id: this.$route.params.customerId,
					action,
					formId: this.form.id
				});

				const currentFilters = {
					...this.$parent.$parent.$parent.defaultFilters,
					...this.$route.query
				};

				await this.getFormsByCustomer({
					id: this.$route.params.customerId,
					...currentFilters
				});
			} finally {
				this.loading = false;
				this.formLocal.enabled = this.form.enabled;
				this.$store.commit('customerForms/setUpdatingStatus', false);
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * Customer Forms Card
		* Can use SCSS variables = TRUE
    *
 */

@import '@/scss/5-objects/_cards.scss';
@import '@/scss/5-objects/_form.scss';

.customer-forms-card {
	@include card;
	display: grid;
	position: relative;
	grid-template-columns: auto 1fr;
	column-gap: $gutter-lg;
	row-gap: $gutter-md;
	align-items: start;
	font-size: $default-font-size - 2;

	&.is-active {
		.customer-forms-card__state {
			@include create-circle(16px, 16px, $ui-positive);
		}
	}

	&.is-locked {
		border-color: $ui-negative;

		.customer-forms-card__state {
			background: none;
			color: $ui-negative;

			.icon {
				font-size: $default-font-size - 6;
			}

			&:hover {
				.tooltip {
					display: inline;
				}
			}
		}
	}

	&__state {
		position: relative;
		color: $white;
		@include vertical-horizontal-center-with-flex;
		@include create-circle(16px, 16px, $neutral-3);
		font-size: 9px;
		z-index: 1;
		cursor: default;

		.tooltip {
			display: none;
		}

		&:hover {
			.tooltip {
				display: inline;
			}
		}
	}

	&__body {
		display: grid;
		grid-template-columns: 1fr;
		gap: $gutter-md;

		@include mq($from: lg) {
			grid-template-columns: 3fr 1fr;
		}
	}

	&__main {
		min-width: 100%;
	}

	&__title {
		width: 100%;
		display: inline-flex;
		flex-direction: row;
		@include heading('md');
		text-transform: capitalize;
	}

	&__divider {
		color: $neutral-3;
		font-weight: 300;
		position: relative;
		top: -1px;
		margin: 0 $gutter-sm;
	}

	&__type {
		display: flex;
		align-items: center;
		align-self: flex-start;
		font-weight: $medium-font-weight;
		color: $neutral-1;

		@include mq($from: lg) {
			grid-column: 2/3;
			justify-content: flex-end;
		}
	}

	&__full-container {
		display: grid;
		gap: $gutter-md;

		@include mq($from: lg) {
			grid-column: 1/3;
		}

		> * {
			min-width: 0;
		}
	}

	&__description {
		font-size: $default-font-size - 3;
		line-height: 16px;

		@include mq($from: lg) {
			grid-column: 1/3;
		}
	}

	&__sync {
		cursor: default;
		text-transform: uppercase;
		font-weight: $medium-font-weight;
		@include text-contrast($page-body-bkgd, $white, $neutral-2);
	}

	&__footer {
		grid-column: 2/3;
		align-self: flex-end;
		display: flex;
		flex-direction: column;

		@include mq($from: lg) {
			flex-direction: row;
			justify-content: space-between;
			align-items: center;
		}
	}

	&__actions {
		margin-top: $gutter-md;

		.actions-set .actions-set__item {
			@include mq($until: sm-l) {
				margin-left: 0;
			}
		}

		@include mq($from: lg) {
			margin-left: auto;
			margin-top: 0;
		}
	}

	.btn-spinner-overlay {
		@include vertical-horizontal-center-with-transform;
	}
}
</style>
